import { COMMUNICATOR_TYPE, SYSTEM_COMPONENT } from 'src/features/systems/utils/types';

export type DeviceConfigValueKey =
    | 'api_key'
    | 'lpr_receiver_address'
    | 'lpr_receiver_ip'
    | 'lpr_receiver_port'
    | 'lpr_receiver_url'
    | 'lpr_receiver_heartbeat_url'
    | 'mqtt_server_ip'
    | 'mqtt_server_port'
    | 'mqtt_username'
    | 'mqtt_password'
    | 'topic_prefix';

export type ComponentDeviceConfig = Partial<Record<DeviceConfigValueKey, string>>;

/**
 * One row of the device-config modal.
 *
 * `label` is deliberately a plain string and NOT a react-intl message: it is the field's name in
 * the vendor's own portal, and translating it would break the one-to-one mapping the installer
 * relies on. `note` is a short untranslated hint shown under the field where the portal needs one.
 */
export type DeviceConfigField = {
    label: string;
    key: DeviceConfigValueKey;
    note?: string;
};

const ICE_CAMERA_FIELDS: DeviceConfigField[] = [
    { label: 'Parking Lot ID', key: 'api_key' },
    { label: 'Server Address,port,path', key: 'lpr_receiver_address' },
];

const IPNC_CAMERA_FIELDS: DeviceConfigField[] = [
    { label: 'Server address', key: 'lpr_receiver_ip' },
    { label: 'Port', key: 'lpr_receiver_port' },
    { label: 'Heartbeat address', key: 'lpr_receiver_heartbeat_url' },
    { label: 'Plate recognition address', key: 'lpr_receiver_url' },
];

const ISAPI1_CAMERA_FIELDS: DeviceConfigField[] = [
    { label: 'ANPR IP', key: 'lpr_receiver_ip' },
    { label: 'ANPR Port', key: 'lpr_receiver_port' },
    {
        label: 'URL',
        key: 'lpr_receiver_url',
        note: 'Set over ISAPI (PUT /ISAPI/Event/notification/httpHosts/1) — this camera has no web page for it.',
    },
];

const EINAR_CAMERA_FIELDS: DeviceConfigField[] = [
    { label: 'URL', key: 'lpr_receiver_url' },
    { label: 'Keep-alive URL', key: 'lpr_receiver_heartbeat_url' },
];

const GATEWAY_CONTROLLER_FIELDS: DeviceConfigField[] = [
    { label: 'IP Address', key: 'mqtt_server_ip' },
    { label: 'Server port', key: 'mqtt_server_port' },
    { label: 'User name', key: 'mqtt_username' },
    { label: 'Password', key: 'mqtt_password' },
    { label: 'Topic prefix', key: 'topic_prefix' },
];

/** Keyed by the component's `data.type`, which is what distinguishes the camera models. */
const DEVICE_CONFIG_FIELDS: Record<string, DeviceConfigField[]> = {
    ice_lpr: ICE_CAMERA_FIELDS,
    ipnc_lpr: IPNC_CAMERA_FIELDS,
    isapi1_lpr: ISAPI1_CAMERA_FIELDS,
    einar_lpr: EINAR_CAMERA_FIELDS,
    [COMMUNICATOR_TYPE.SP_MQTT]: GATEWAY_CONTROLLER_FIELDS,
};

/**
 * The rows to render for a component, or undefined when it has no external portal to configure.
 * Gate the side-panel button on this returning a value.
 *
 * Takes the two primitives rather than the component object: `SystemComponent['data']` is a union
 * whose members do not all carry `type`, so a structural parameter would not narrow cleanly.
 */
export function getDeviceConfigFields(componentType?: string, dataType?: string): DeviceConfigField[] | undefined {
    if (!dataType) return undefined;

    const supported =
        componentType === SYSTEM_COMPONENT.LPR_CAMERA || componentType === SYSTEM_COMPONENT.GATEWAY_COMMUNICATOR;

    if (!supported) return undefined;

    return DEVICE_CONFIG_FIELDS[dataType];
}

/** Camera configs mint a fresh key on every call; the gateway controller config does not. */
export function deviceConfigRotatesApiKey(componentType?: string): boolean {
    return componentType === SYSTEM_COMPONENT.LPR_CAMERA;
}

export { DEVICE_CONFIG_FIELDS };
